import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const DEFAULT_APP_URL = 'https://app.kulum.jp';

interface StartImpersonationRequest {
  targetUserId: string;
  reason: string;
}

interface StartImpersonationResponse {
  customToken: string;
  sessionId: string;
  targetUserId: string;
  targetEmail: string;
  expiresAt: string;
}

export async function startUserImpersonation(
  targetUserId: string,
  reason: string
): Promise<StartImpersonationResponse> {
  const callable = httpsCallable<StartImpersonationRequest, StartImpersonationResponse>(
    functions,
    'startImpersonation'
  );
  const result = await callable({ targetUserId, reason }).catch((error: unknown) => {
    throw new Error(getImpersonationErrorMessage(error));
  });
  const data = result.data;

  if (!data.customToken || !data.sessionId) {
    throw new Error('代理ログイントークンの発行に失敗しました');
  }

  return data;
}

export function buildImpersonationUrl(customToken: string, sessionId: string): string {
  const baseUrl = import.meta.env.VITE_APP_URL?.trim() || DEFAULT_APP_URL;
  const url = new URL('/impersonate', baseUrl);
  url.hash = new URLSearchParams({
    token: customToken,
    sessionId,
  }).toString();
  return url.toString();
}

function getImpersonationErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return '代理ログインの開始に失敗しました';
  }

  const code = 'code' in error ? (error as { code?: unknown }).code : undefined;
  const message = 'message' in error ? (error as { message?: unknown }).message : undefined;

  if (typeof code === 'string') {
    switch (code) {
      case 'functions/unauthenticated':
        return '管理画面に再ログインしてください';
      case 'functions/permission-denied':
        return '代理ログインを開始する権限がありません';
      case 'functions/not-found':
        return '対象ユーザーが見つかりません';
      case 'functions/failed-precondition':
        return 'このユーザーには代理ログインできません';
      case 'functions/invalid-argument':
        return '代理ログインの理由を入力してください';
      case 'functions/internal':
        return '代理ログイントークンの発行に失敗しました';
      default:
        break;
    }
  }

  if (typeof message === 'string' && message.trim() && message !== 'INTERNAL') {
    return message;
  }

  return '代理ログインの開始に失敗しました';
}
